const SELLING_PLAN_FIELD_NAME = 'selling_plan';
const STORAGE_KEY = `selectedSellingPlan_${window.location.hostname}`;
const PLAN_CHANGE_EVENT = 'planChange';
const LEGACY_WIDGET_SELECTOR = '#appstle_subscription_widget0';

// Bridge inline change handlers with the custom element event flow.
function handleSellingPlanChange(event) {
  const component = event.target.closest('selling-plans');
  if (component) {
    component.dispatchEvent(new CustomEvent(PLAN_CHANGE_EVENT));
  }
}

// Handle subscription-specific changes within the subscriptions tab
function handleSubscriptionChange(event) {
  const component = event.target.closest('selling-plans');
  if (!component) {
    console.warn('Selling plans component not found');
    return;
  }

  const subscriptionInput = event.target;
  if (!subscriptionInput) {
    console.warn('Subscription input not found');
    return;
  }

  const subscriptionId = subscriptionInput.dataset.subscriptionId;
  const variantId = subscriptionInput.dataset.variantId;
  const normalPrice = subscriptionInput.dataset.normalPrice;
  const compareAtPrice = subscriptionInput.dataset.compareAtPrice;
  const description = subscriptionInput.dataset.description || '';

  if (!subscriptionId || !variantId) {
    console.warn('Missing subscription data:', { subscriptionId, variantId });
    return;
  }

  // Find and check the main subscription radio button
  const mainSubscriptionRadio = component.querySelector('[data-radio="subscriptions"]');
  if (mainSubscriptionRadio) {
    mainSubscriptionRadio.checked = true;
    // Update the main radio's data attributes to match the selected subscription
    mainSubscriptionRadio.dataset.subscription = subscriptionId;
    mainSubscriptionRadio.dataset.variantId = variantId;
    mainSubscriptionRadio.dataset.normalPrice = normalPrice;
    mainSubscriptionRadio.dataset.compareAtPrice = compareAtPrice || '';
  } else {
    console.warn('Main subscription radio not found');
  }

  // Update the description if it exists
  const descriptionElement = component.querySelector('[data-subscription-description]');
  if (descriptionElement) {
    if (description) {
      descriptionElement.innerHTML = description;
      descriptionElement.style.display = 'block';
    } else {
      descriptionElement.style.display = 'none';
    }
  }

  // Trigger the plan change handler to update form and prices
  component.dispatchEvent(new CustomEvent(PLAN_CHANGE_EVENT));
}

// Make function globally accessible
window.handleSubscriptionChange = handleSubscriptionChange;

// Remove third-party subscription widgets that clash with the native component.
function removeOldSellingPlanWidget() {
  setTimeout(() => {
    const widget = document.querySelector(LEGACY_WIDGET_SELECTOR);
    if (widget) {
      console.log('Removing old subscription widget:', widget);
      widget.remove();
    }
  }, 500);
}

removeOldSellingPlanWidget();

if (!customElements.get('selling-plans')) {
  class SellingPlans extends HTMLElement {
    constructor() {
      super();
      this.handlePlanChange = this.handlePlanChange.bind(this);
      this.handleActButtonClick = this.handleActButtonClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.actButton = document.querySelector('[data-act-button]');
      this.variantChangeUnsubscriber = undefined;
    }

    connectedCallback() {
      this.cacheDomReferences();
      this.addEventListener(PLAN_CHANGE_EVENT, this.handlePlanChange);
      this.attachSubscriptionListeners();
      this.initializeSubscriptions();
      this.restoreSelection();
      this.subscribeToVariantChanges();

      if (this.actButton) {
        this.actButton.addEventListener('click', this.handleActButtonClick);
      }
    }

    subscribeToVariantChanges() {
      // Subscribe to variant change events to update selling plan options
      // subscribe and PUB_SUB_EVENTS should be available globally from pubsub.js and constants.js
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }
    }

    handleVariantChange({ data: { variant } }) {
      if (!variant) return;

      // Update the one-time purchase radio button with the new variant's data
      const oneTimeRadio = this.querySelector('[data-radio="one-time"]');
      if (oneTimeRadio) {
        // Format prices without currency symbol for data attributes (German format: comma as decimal separator)
        const normalPrice = this.formatPriceForData(variant.price);
        const compareAtPrice = variant.compare_at_price ? this.formatPriceForData(variant.compare_at_price) : '';

        oneTimeRadio.dataset.variantId = variant.id.toString();
        oneTimeRadio.dataset.normalPrice = normalPrice;
        oneTimeRadio.dataset.compareAtPrice = compareAtPrice;

        // Update aria-label with formatted price
        const priceFormatted = this.formatPriceWithCurrency(variant.price);
        oneTimeRadio.setAttribute('aria-label', `One-time purchase. Product price ${priceFormatted}`);

        // If one-time is currently selected, update the price display
        if (oneTimeRadio.checked) {
          this.handlePlanChange();
        }
      }
    }

    formatPriceForData(price) {
      // Convert price from cents to formatted string without currency
      // Shopify variant.price is in cents, format as German locale (comma as decimal separator)
      const priceInCurrency = price / 100;
      // Format with comma as decimal separator (e.g., "21,95")
      return priceInCurrency.toFixed(2).replace('.', ',');
    }

    formatPriceWithCurrency(price) {
      // Convert price from cents to formatted string with currency (e.g., "21,95€")
      const priceInCurrency = price / 100;
      return `${priceInCurrency.toFixed(2).replace('.', ',')}€`;
    }

    attachSubscriptionListeners() {
      // Attach event listeners to all subscription radio inputs
      // Use a more specific selector to target subscription inputs
      const subscriptionContainer = this.querySelector('.selling-plans__subscriptions');
      if (!subscriptionContainer) return;

      const subscriptionInputs = subscriptionContainer.querySelectorAll('input[type="radio"]');
      subscriptionInputs.forEach(input => {
        // Use both change and click events to ensure it works
        input.addEventListener('change', (event) => {
          handleSubscriptionChange(event);
        });
        input.addEventListener('click', (event) => {
          // Ensure the input is checked when clicked directly
          if (!event.target.checked) {
            event.target.checked = true;
            event.target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    initializeSubscriptions() {
      // Find the checked subscription input and sync it with the main radio
      const checkedSubscription = this.querySelector('.selling-plans__input:checked');
      if (checkedSubscription) {
        const subscriptionId = checkedSubscription.dataset.subscriptionId;
        const variantId = checkedSubscription.dataset.variantId;
        const normalPrice = checkedSubscription.dataset.normalPrice;
        const compareAtPrice = checkedSubscription.dataset.compareAtPrice;
        const description = checkedSubscription.dataset.description || '';

        const mainSubscriptionRadio = this.querySelector('[data-radio="subscriptions"]');
        if (mainSubscriptionRadio) {
          mainSubscriptionRadio.dataset.subscription = subscriptionId;
          mainSubscriptionRadio.dataset.variantId = variantId;
          mainSubscriptionRadio.dataset.normalPrice = normalPrice;
          mainSubscriptionRadio.dataset.compareAtPrice = compareAtPrice || '';
        }

        // Update the description if it exists
        const descriptionElement = this.querySelector('[data-subscription-description]');
        if (descriptionElement && description) {
          descriptionElement.innerHTML = description;
          descriptionElement.style.display = 'block';
        }
      }
    }

    disconnectedCallback() {
      this.removeEventListener(PLAN_CHANGE_EVENT, this.handlePlanChange);
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
      if (this.actButton) {
        // this.actButton.removeEventListener('click', this.handleActButtonClick);
      }
    }

    cacheDomReferences() {
      this.radios = this.querySelectorAll('[data-radio]');
      this.tabs = this.querySelectorAll('[data-tab]');
      this.productInfo = this.productInfo || this.closest('product-info') || document;
      const scope = this.productInfo || document;
      this.form = scope.querySelector('[data-type="add-to-cart-form"]') || document.querySelector('[data-type="add-to-cart-form"]');
      this.productVariantId = this.form?.querySelector('.product-variant-id') || null;
      this.regularPrice = scope.querySelector('[data-regular-price]') || null;
      this.salePrice = scope.querySelector('[data-sale-price]') || null;
      this.priceBadges = scope.querySelectorAll('[data-price-badge]') || null;
    }

    handlePlanChange() {
      this.cacheDomReferences();
      this.radios.forEach((radio) => {
        if (!radio.checked) return;
        this.persistSelection(radio);
        this.toggleTabs(radio);
        this.syncVariant(radio);
        this.updatePriceDisplay(radio);
      });
      removeOldSellingPlanWidget();
    }

    persistSelection(radio) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ type: radio.dataset.radio }));
    }

    toggleTabs(radio) {
      if (!this.tabs.length) return;
      this.tabs.forEach((tab) => tab.removeAttribute('data-open'));
      const targetTab = this.querySelector(`[data-tab="${radio.dataset.radio}"]`);
      if (targetTab) {
        targetTab.setAttribute('data-open', '');
      }
    }

    syncVariant(radio) {
      if (!this.form || !this.productVariantId) return;

      if (radio.dataset.radio === 'subscriptions') {
        const input = this.getOrCreateSellingPlanInput();
        input.value = radio.dataset.subscription;
        input.setAttribute('data-selected-selling-plan', radio.dataset.subscription);
      } else {
        const existingInput = this.form.querySelector(`input[name="${SELLING_PLAN_FIELD_NAME}"]`);
        if (existingInput) {
          existingInput.remove();
        }
      }

      this.productVariantId.value = radio.dataset.variantId;
    }

    getOrCreateSellingPlanInput() {
      let input = this.form.querySelector(`input[name="${SELLING_PLAN_FIELD_NAME}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = SELLING_PLAN_FIELD_NAME;
        this.form.prepend(input);
      }
      return input;
    }

    restoreSelection() {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
          const { type } = JSON.parse(saved) || {};
          const radio = type ? this.querySelector(`[data-radio="${type}"]`) : null;
          if (radio) {
            radio.checked = true;
          }
        }
      } catch (error) {
        console.warn('Failed to restore selling plan selection:', error);
      }
      this.handlePlanChange();
    }

    // Swap between sale and regular price states based on the selected plan metadata.
    updatePriceDisplay(radio) {
      const scope = this.productInfo || document;
      this.regularPrice = this.regularPrice || scope.querySelector('[data-regular-price]');
      this.salePrice = this.salePrice || scope.querySelector('[data-sale-price]');

      if (!this.regularPrice && !this.salePrice) return;

      const normalPriceRaw = radio.dataset.normalPrice;
      const compareAtPriceRaw = radio.dataset.compareAtPrice || radio.dataset.compareAtPrice;
      const normalPrice =
        typeof normalPriceRaw === 'string' ? normalPriceRaw.trim() : normalPriceRaw;
      const compareAtPrice =
        typeof compareAtPriceRaw === 'string' ? compareAtPriceRaw.trim() : compareAtPriceRaw;
      if (!normalPrice) return;

      const hasCompareAtPrice =
        Boolean(compareAtPrice) && compareAtPrice !== '' && compareAtPrice !== normalPrice;

      if (this.regularPrice) {
        this.regularPrice.dataset.regularPrice = !hasCompareAtPrice;
      }
      if (this.salePrice) {
        this.salePrice.dataset.salePrice = hasCompareAtPrice;
      }

      if (!hasCompareAtPrice) {
        this.renderRegularPrice(normalPrice);
        return;
      }

      this.renderSalePrice(normalPrice, compareAtPrice);
    }

    renderRegularPrice(value) {
      if (!this.regularPrice) return;
      this.clearElement(this.regularPrice);
      this.regularPrice.appendChild(
        this.createPriceNode(value, {
          tag: 'span',
          className: 'price-item price-item--regular'
        })
      );

      // Adjust price badges margin based on width difference
      if (this.priceBadges && this.priceBadges.length) {
        requestAnimationFrame(() => {
          const wrapperWidth = this.regularPrice.parentElement?.offsetWidth || 0;
          const priceItemWidth = this.regularPrice.offsetWidth || 0;
          const offset = wrapperWidth - priceItemWidth;
          this.priceBadges.forEach(badge => {
            badge.style.marginLeft = `-${offset}px`;
          });
        });
      }
    }

    renderSalePrice(normalPrice, compareAtPrice) {
      if (!this.salePrice) return;
      this.clearElement(this.salePrice);

      const compareWrapper = document.createElement('span');
      compareWrapper.appendChild(
        this.createPriceNode(compareAtPrice, {
          tag: 's',
          className: 'price-item price-item--regular'
        })
      );
      this.salePrice.appendChild(compareWrapper);

      this.salePrice.appendChild(
        this.createPriceNode(normalPrice, {
          tag: 'span',
          className: 'price-item price-item--sale price-item--last'
        })
      );

      // Reset price badges margin
      if (this.priceBadges && this.priceBadges.length) {
        this.priceBadges.forEach(badge => {
          badge.style.marginLeft = '0';
        });
      }
    }

    createPriceNode(value, { tag, className }) {
      const node = document.createElement(tag);
      if (className) {
        node.className = className;
      }
      // Ensure a trailing currency symbol for prices coming from dataset values
      // (which are stored without currency, e.g. "18,66").
      const hasEuro = typeof value === 'string' && value.trim().endsWith('€');
      const displayValue = hasEuro ? value.trim() : `${value}€`;
      node.textContent = displayValue;
      return node;
    }

    handleActButtonClick(event) {
      event.preventDefault();
      this.addToCartWithSellingPlan().catch(error => {
        console.error('Error adding current selection with selling plan to cart:', error);
      });
    }

    addToCartWithSellingPlan() {
      if (!this.form || !this.productVariantId) {
        return Promise.reject(new Error('Form or variant input not found'));
      }

      const variantId = this.productVariantId.value;
      const quantityInput =
        this.form.querySelector('input[name="quantity"], input[data-cart-quantity]');
      const sellingPlanInput = this.form.querySelector(
        `input[name="${SELLING_PLAN_FIELD_NAME}"][data-selected-selling-plan]`
      );

      const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
      const sellingPlanId = sellingPlanInput ? sellingPlanInput.value : null;

      if (!variantId) {
        return Promise.reject(new Error('No variant ID selected'));
      }

      const item = {
        id: variantId,
        quantity
      };

      if (sellingPlanId) {
        item.selling_plan = sellingPlanId;
      }

      console.log('item', item);

      const body = JSON.stringify({
        items: [item],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      });

      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      return fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body
      })
        .then(response => {
          if (!response.ok) {
            return response.text().then(text => {
              throw new Error(
                `HTTP error! status: ${response.status}, message: ${text}`
              );
            });
          }
          return response.json();
        })
        .then(data => {
          console.log('addToCartWithSellingPlan response:', data);
          if (data.sections) {
            this.updateCartUIFromSections(data.sections);
          } else {
            this.refreshCartUI();
          }
          return data;
        });
    }

    updateCartUIFromSections(sections) {
      const cartDrawer = document.querySelector('cart-drawer');

      try {
        if (sections['cart-drawer'] && cartDrawer) {
          const html = new DOMParser().parseFromString(
            sections['cart-drawer'],
            'text/html'
          );
          const updatedCartDrawer = html.querySelector('cart-drawer');

          if (updatedCartDrawer) {
            const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');
            const newDrawerInner = updatedCartDrawer.querySelector('.drawer__inner');

            if (currentDrawerInner && newDrawerInner) {
              currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
            }

            cartDrawer.classList.remove('is-empty');
          }
        }

        if (sections['cart-icon-bubble']) {
          const html = new DOMParser().parseFromString(
            sections['cart-icon-bubble'],
            'text/html'
          );
          const updatedCartIcon = html.querySelector('#cart-icon-bubble');
          const currentCartIcon = document.querySelector('#cart-icon-bubble');

          if (updatedCartIcon && currentCartIcon) {
            currentCartIcon.innerHTML = updatedCartIcon.innerHTML;
          }
        }

        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      } catch (error) {
        console.error('Error updating cart UI from sections:', error);
        this.refreshCartUI();
      }
    }

    refreshCartUI() {
      const cartDrawer = document.querySelector('cart-drawer');
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      // Update cart drawer
      fetch(`${root}?section_id=cart-drawer`)
        .then(response => response.text())
        .then(responseText => {
          if (!cartDrawer) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const updatedCartDrawer = html.querySelector('cart-drawer');

          if (updatedCartDrawer) {
            const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');
            const newDrawerInner = updatedCartDrawer.querySelector('.drawer__inner');

            if (currentDrawerInner && newDrawerInner) {
              currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
            }

            cartDrawer.classList.remove('is-empty');
          }

          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        })
        .catch(error => {
          console.error('Error refreshing cart drawer UI:', error);
        });

      // Update cart icon bubble
      fetch(`${root}?section_id=cart-icon-bubble`)
        .then(response => response.text())
        .then(responseText => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const updatedCartIcon = html.querySelector('#cart-icon-bubble');
          const currentCartIcon = document.querySelector('#cart-icon-bubble');

          if (updatedCartIcon && currentCartIcon) {
            currentCartIcon.innerHTML = updatedCartIcon.innerHTML;
          }
        })
        .catch(error => {
          console.error('Error refreshing cart icon UI:', error);
        });
    }

    clearElement(element) {
      while (element && element.firstChild) {
        element.innerHTML = '';
      }
    }
  }

  customElements.define('selling-plans', SellingPlans);
}
